import { MutateWidgetPayload, WidgetDto } from '@thingslog/repositories';
import { QueryKeys } from '@thingslog/queries';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { dashboardWidgetsQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';
import { WidgetSettingsFactory } from '../factories/WidgetSettings.factory';

interface UseDashboardWidgetOperationsResult {
  handlePersistNewWidget: (
    payload: MutateWidgetPayload,
    callbacks?: DashboardWidgetMutationCallbacks
  ) => void;
  handlePersistWidget: (
    widgetId: string,
    payload: MutateWidgetPayload,
    callbacks?: DashboardWidgetMutationCallbacks
  ) => void;
  handleDeleteWidget: (widget: WidgetDto, callbacks?: DashboardWidgetMutationCallbacks) => void;
  isPersistingNewWidget: boolean;
  isPersistingWidget: boolean;
  isDeletingWidget: boolean;
}

interface DashboardWidgetMutationCallbacks {
  onSuccess?: () => void;
}

export const useDashboardWidgetOperations = (
  dashboardId: string,
  companyId?: string | number | null
): UseDashboardWidgetOperationsResult => {
  const { useCreateDashboardWidget, useUpdateDashboardWidget, useDeleteDashboardWidget } =
    dashboardWidgetsQueryClient;
  const queryClient = useQueryClient();

  const createWidgetMutation = useCreateDashboardWidget({
    onSuccess: (
      _widget: WidgetDto,
      variables: {
        dashboardId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }
    ): void => {
      queryClient.invalidateQueries([
        QueryKeys.GetDashboard,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetWidgets,
        variables.dashboardId,
        variables.companyId,
      ]);
    },
  });

  const updateWidgetMutation = useUpdateDashboardWidget({
    onSuccess: (
      _widget: WidgetDto,
      variables: {
        dashboardId: string;
        widgetId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }
    ): void => {
      queryClient.invalidateQueries([
        QueryKeys.GetDashboard,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetWidgets,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetWidget,
        variables.dashboardId,
        variables.widgetId,
        variables.companyId,
      ]);
    },
  });

  const deleteWidgetMutation = useDeleteDashboardWidget({
    onSuccess: (
      _deleted: boolean,
      variables: {
        dashboardId: string;
        widgetId: string;
        companyId?: string | number | null;
      }
    ): void => {
      queryClient.invalidateQueries([
        QueryKeys.GetDashboard,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetWidgets,
        variables.dashboardId,
        variables.companyId,
      ]);
      queryClient.invalidateQueries([
        QueryKeys.GetWidget,
        variables.dashboardId,
        variables.widgetId,
        variables.companyId,
      ]);
    },
  });

  const handlePersistNewWidget = useCallback(
    (payload: MutateWidgetPayload, callbacks?: DashboardWidgetMutationCallbacks): void => {
      if (!dashboardId) {
        return;
      }

      createWidgetMutation.mutate(
        {
          dashboardId,
          body: { settings: WidgetSettingsFactory.normalize(payload.settings) },
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, createWidgetMutation]
  );

  const handlePersistWidget = useCallback(
    (
      widgetId: string,
      payload: MutateWidgetPayload,
      callbacks?: DashboardWidgetMutationCallbacks
    ): void => {
      if (!dashboardId) {
        return;
      }

      updateWidgetMutation.mutate(
        {
          dashboardId,
          widgetId,
          body: { settings: WidgetSettingsFactory.normalize(payload.settings) },
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, updateWidgetMutation]
  );

  const handleDeleteWidget = useCallback(
    (widget: WidgetDto, callbacks?: DashboardWidgetMutationCallbacks): void => {
      if (!dashboardId) {
        return;
      }

      deleteWidgetMutation.mutate(
        {
          dashboardId,
          widgetId: widget.id,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [dashboardId, companyId, deleteWidgetMutation]
  );

  return {
    handlePersistNewWidget,
    handlePersistWidget,
    handleDeleteWidget,
    isPersistingNewWidget: createWidgetMutation.isLoading,
    isPersistingWidget: updateWidgetMutation.isLoading,
    isDeletingWidget: deleteWidgetMutation.isLoading,
  };
};
